import { createContext, useContext, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { cloneDeep } from 'lodash';

import { useAudienceVisitorsTableMapData } from "./hooks/useAudienceVisitorsTableMapData";
import { useGlobal } from "@/migrated-widgets/table-map/contexts/GlobalState";
import { useAudienceVisitorsMarkers } from "./hooks/useAudienceVisitorsMarkers";
import { useWalkieTalkie } from "@/migrated-widgets/table-map/hooks/useWalkieTalkie";
import { useWidgetData } from "@/app/widgets/WidgetFactoryContext";
import { AudienceVisitorsTableMapContextType, ITotals } from "./types";
import { useDealerMarkers } from "@/migrated-widgets/table-map/hooks/shared/useDealerMarkers";
import { Audience } from "@/migrated-widgets/table-map/services/MapDataLayer/types/Engage.types";
import { audienceRecordUniqueId } from "./constants";
import { useAudienceGroups } from "./hooks/useAudienceGroups";
import { useLocationData } from "./hooks/useLocationData";
import { GetLocationDataReturn } from "@/migrated-widgets/table-map/services/PortalV2/types";
import { locationProducts, productIds } from "../TableMap/constants";


const AudienceTableVisitorsMapContext = createContext<AudienceVisitorsTableMapContextType>({

    setZipCodes: () => { },
    setAudiencesIds: () => { },
    audiencesIds: [],
    loadingAudiences: false,
    totals: { totalVisitors: 0, newModelVisitors: 0, otherVisitors: 0 },
    audienceMarkers: [],
    zipCodes: [],
    walkieTalkie: { listen: () => { }, send: () => { } },
    loadingDealers: false,
    dealerMarkers: [],
    vehicleTypes: [],
    audienceGroups: [],
    setVehicleTypes: () => { },
    groupIds: [],
    makeIds: [],
    modelIds: [],
    resetZoom: true,
    setGroupIds: () => { },
    setMakeIds: () => { },
    setModelIds: () => { },
    setResetZoom: () => { },
    loadingLocations: false,
    locations: undefined,
    circleMarkers: [],
    geometryFeatures: [],
    distinctProducts: [],
    products: [],
    setProducts: () => { },
    currentlyViewing: null,
})


const AudienceVisitorsTableMapProvider: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const walkieTalkie = useWalkieTalkie();
    const { setNoData } = useWidgetData();
    const { params } = useGlobal();
    const { audiences, loadingAudiences } = useAudienceVisitorsTableMapData();

    const audienceGroups = useAudienceGroups(audiences);

    const {
        audienceMarkers, zipCodes, setZipCodes, audiencesIds,
        vehicleTypes, setAudiencesIds, setVehicleTypes,
        groupIds, makeIds, resetZoom, modelIds, setMakeIds, setModelIds, setGroupIds, setResetZoom
    } = useAudienceVisitorsMarkers(audiences);

    const { dealerMarkers, loadingDealers } = useDealerMarkers();
    const { locations, loadingLocations } = useLocationData();

    const [products, setProducts] = useState<string[]>([]);
    const [circleMarkersSource, setCircleMarkersSource] = useState<
        GetLocationDataReturn["circleMarkers"]
    >([]);
    const [geometryFeaturesSource, setGeometryFeaturesSource] = useState<
        GetLocationDataReturn["geometryFeatures"]
    >([]);



    const currentlyViewing = useMemo(() => {
        if (vehicleTypes.length || audiencesIds.length) return 'Custom Selection';

        return null;
    }, [vehicleTypes, audiencesIds])

    const [newModelAudiences, otherAudiences] = useMemo(() => {
        const granularAudiences = audiences.flatMap(_audience => _audience.dataCollection);
        const _audiences = granularAudiences
            .filter((_audience) => zipCodes.length ? zipCodes.includes(_audience.data.audienceVisitorReportZipCode) : true);

        const audienceGroupMap = (audiencesGroup: _.Dictionary<Audience['dataCollection']>): Audience[] => {
            return Object.keys(audiencesGroup).map((_audience) => {
                const value = audiencesGroup[_audience];
                return {
                    audienceId: value[0].data.audienceId,
                    visitorsCount: value.reduce((acc, crr) => acc + crr.data.visitorsCount, 0),
                    audienceModelId: value[0].data.audienceVisitorReportModelId,
                    audienceName: value[0].data.audienceName,
                    audienceMakeId: value[0].data.audienceVisitorReportMakeId,
                    clientId: 0,
                    dataCollection: value
                }
            })
        }

        const _newModelAudiences = _.groupBy(_audiences.filter(audience => audience.data.audienceVisitorReportModelId), audienceRecordUniqueId)
        const _otherAudiences = _.groupBy(_audiences.filter(audience => !audience.data.audienceVisitorReportModelId), audienceRecordUniqueId)

        return [audienceGroupMap(_newModelAudiences), audienceGroupMap(_otherAudiences)];
    }, [audiences, zipCodes]);

    const totals = useMemo((): ITotals => {
        const _newModelTotals = newModelAudiences.reduce((acc, crr) => acc + crr.visitorsCount, 0);
        const _otherTotals = otherAudiences.reduce((acc, crr) => acc + crr.visitorsCount, 0);
        const totalVisitors = audienceGroups.reduce((acc, group) => acc + group.visitorsCount, 0);

        return { totalVisitors, newModelVisitors: _newModelTotals, otherVisitors: _otherTotals };
    }, [audienceGroups, newModelAudiences, otherAudiences]);

    const circleMarkersByProduct = useMemo(() => {
        if (!products.length) return circleMarkersSource;
        const productsIds = products.map((product: string) => productIds[product]);

        const markers = cloneDeep(circleMarkersSource)
            .filter((marker) => marker.locations.some((location) => !productsIds.includes(productIds[location.product])));
        markers.forEach((marker) => marker.locations = marker.locations.filter((location) => !productsIds.includes(productIds[location.product])));
        return markers;
    }, [circleMarkersSource, products]);

    const geometryFeaturesByProduct = useMemo(() => {
        if (!products.length) return geometryFeaturesSource;
        const productsIds = products.map((product: string) => productIds[product]);

        const features = cloneDeep(geometryFeaturesSource)
            .filter((feature) => feature.locations.some((location) => !productsIds.includes(productIds[location.product])));
        features.forEach((feature) => feature.locations = feature.locations.filter((location) => !productsIds.includes(productIds[location.product])));
        return features;
    }, [geometryFeaturesSource, products]);

    const distinctProducts = useMemo(() => {
        const locationProductsFilter = [...circleMarkersSource, ...geometryFeaturesSource]
            .flatMap((marker) => marker.locations.map((location) => productIds[location.product]))
            .filter((location) => location)
            .map((productId) => locationProducts.find(location => location.id === productId));

        const productNames = locationProductsFilter.map((product) => product?.value)

        return Array.from(new Set(productNames)).filter((name): name is string => name !== undefined);
    }, [circleMarkersSource, geometryFeaturesSource]);

    useEffect(() => {
        if (!loadingAudiences) {
            if (!audiences.length && params.widgetId) {
                walkieTalkie.send({ event: 'force-close' }, params.widgetId);
                setNoData(true);
            }
        }
    }, [loadingAudiences]);

    useEffect(() => {
        if (locations?.circleMarkers) {
            setCircleMarkersSource(locations.circleMarkers);
        }
    }, [locations]);

    useEffect(() => {
        if (locations?.geometryFeatures) {
            setGeometryFeaturesSource(locations.geometryFeatures);
        }
    }, [locations]);

    useEffect(() => {
        if (distinctProducts.length > 0) {
            setProducts(distinctProducts);
        }
    }, [distinctProducts]);

    return (
        <AudienceTableVisitorsMapContext.Provider value={{
            audienceMarkers,
            loadingAudiences,
            totals,
            setZipCodes,
            zipCodes,
            walkieTalkie,
            audiencesIds,
            setAudiencesIds,
            dealerMarkers,
            loadingDealers,
            vehicleTypes,
            setVehicleTypes,
            audienceGroups,
            makeIds,
            modelIds,
            groupIds,
            resetZoom,
            setMakeIds,
            setModelIds,
            setGroupIds,
            setResetZoom,
            loadingLocations,
            locations,
            circleMarkers: circleMarkersByProduct,
            geometryFeatures: geometryFeaturesByProduct,
            distinctProducts,
            products,
            setProducts,

            currentlyViewing
        }}>
            {children}
        </AudienceTableVisitorsMapContext.Provider>
    )
}

function useAudienceVisitorsTableMapState() {
    return useContext(AudienceTableVisitorsMapContext);
}

export {
    AudienceTableVisitorsMapContext,
    AudienceVisitorsTableMapProvider,
    useAudienceVisitorsTableMapState,
};
